import {
  of,
  map,
  tap,
  Subject,
  Observable,
  catchError,
  throwError,
  BehaviorSubject,
} from 'rxjs';
import { environment } from '@utility/app.util';

import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpParams } from '@angular/common/http';

import { NotificationService } from '@utility/services/notification.service';
import { AbstractAuthService } from '@sociuuhub/modules/auth/services//abstract-auth.service';

import {
  RefreshTokenDto,
  LoginResponseDto,
  DefaultResponseDto,
} from '@sociuu/interfaces';
import { ISignupServer } from '@lib/self-signup.interface';

export interface ISociuuhubUser {
  user: string;
  companyName: string;
}

const SECOND: number = 1000;
const SOCIAL_CONNECT_LINKEDIN_STATUS_POLL_TIMEOUT: number = 2 * SECOND;

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractAuthService {
  private helper = new JwtHelperService();
  private pollTimer: ReturnType<typeof window.setTimeout>;
  private _currentUser$ = new BehaviorSubject<ISociuuhubUser>(null);
  private _linkedInStatusData$: Subject<boolean> = new BehaviorSubject<boolean>(null);

  public get isTokenExpired(): boolean {
    return this.helper.isTokenExpired(this.token);
  }

  public get decodeToken(): any {
    return this.helper.decodeToken(this.token);
  }

  public get isBecomeUserEnabled(): boolean {
    return localStorage.getItem('isBecomeUser') === 'true';
  }

  public get linkedInStatusData(): Observable<boolean> {
    return this._linkedInStatusData$.asObservable();
  }

  public get currentUser(): BehaviorSubject<ISociuuhubUser> {
    return this._currentUser$;
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private notificationService: NotificationService,
  ) {
    super();
  }

  public set token(token: string) {
    localStorage.setItem('tokenUser', token);
  }

  public get token(): string {
    return localStorage.getItem('tokenUser');
  }

  public signup(payload: any): Observable<ISignupServer> {
    return this.http.post<{ data: ISignupServer }>(`${environment.sociuuhubApiUrl}/user/signup`, payload)
      .pipe(
        map((response: { data: ISignupServer }): ISignupServer => response.data),
      );
  }

  public socialConnectStatus(payload: { token: string }): Observable<boolean> {
    return this.http
      .post<{ data: { is_connected: boolean } }>(`${environment.sociuuhubApiUrl}/signup/social-connect/status`, payload)
      .pipe(
        map((response: { data: { is_connected: boolean } }): boolean => response.data.is_connected),
        tap((response: boolean) => this._linkedInStatusData$.next(response)),
        catchError((err) => {
          if (!err.data) this.notificationService.showError(err);
          return throwError(() => err);
        }),
      );
  }

  public startPollingOfLinkedInStatus(token: string): void {
    this.socialConnectStatus({ token }).pipe(
      catchError((err) => {
        this.stopPolling();
        if (!err.data) this.notificationService.showError(err);
        return of(null);
      })
    ).subscribe();
    this.pollTimer = setTimeout(() => this.startPollingOfLinkedInStatus(token), SOCIAL_CONNECT_LINKEDIN_STATUS_POLL_TIMEOUT);
  }

  public stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  public sendMagicLink(field: string, type: 'email' | 'number' = 'email', returnURL?: string): Observable<any> {
    const queryParams: string = `${type === 'email' ? 'email' : 'phone'}=${field}${returnURL ? `&returnUrl=${returnURL}` : ''}`;
    return this.http.get(
      `${environment.sociuuhubApiUrl}/user/send-magic-link?${queryParams}`
    );
  }

  public verifyUser(
    signature: string,
    expires: string,
    token: string
  ): Observable<any> {
    return this.http.get(
      `${environment.sociuuhubApiUrl}/user/verify-login?token=${token}`
    );
  }

  public login(email: string, password: string): Observable<LoginResponseDto> {
    const data = {
      email: email,
      password: password,
    };

    return this.http.post<LoginResponseDto>(
      `${environment.sociuuhubApiUrl}/login`,
      data
    );
  }

  public refreshUserToken(): Observable<RefreshTokenDto> {
    return this.http.get<RefreshTokenDto>(`${environment.sociuuhubApiUrl}/auth/refresh`);
  }

  public logout(): void {
    this._currentUser$.next(null);
    this._linkedInStatusData$.next(null);

    localStorage.removeItem('tokenUser');
    localStorage.removeItem('isBecomeUser');
    localStorage.removeItem('becomeUserByTypeID');
    localStorage.removeItem('becomeUserExpireTime');

    this.router.navigate(['/sociuuhub/login']);
  }

  public forceRefresh(): void {
    location.reload();
  }

  public validateUserToken(
    token: string,
    type: string
  ): Observable<DefaultResponseDto> {
    let params = new HttpParams();
    params = params.append('token', `${token}`);
    params = params.append('type', `${type}`);
    return this.http.get<DefaultResponseDto>(
      `${environment.sociuuhubApiUrl}/account/validate`,
      { params: params }
    );
  }
}
